import axios from 'axios';

const API_BASE_URL =
  process.env.REACT_APP_API_URL ||
  'https://api.icingshow.com/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json'
  }
});

/*
 * Add JWT access token to requests.
 */
api.interceptors.request.use(
  (config) => {
    const token =
      localStorage.getItem(
        'access_token'
      );

    if (token) {
      config.headers.Authorization =
        `Bearer ${token}`;
    }

    return config;
  },
  (error) =>
    Promise.reject(error)
);

/*
 * Refresh expired access token once.
 */
api.interceptors.response.use(
  (response) => response,

  async (error) => {
    const originalRequest =
      error.config;

    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true;

      const refreshToken =
        localStorage.getItem(
          'refresh_token'
        );

      if (!refreshToken) {
        localStorage.removeItem(
          'access_token'
        );

        localStorage.removeItem(
          'user'
        );

        window.location.href =
          '/login';

        return Promise.reject(error);
      }

      try {
        const response =
          await axios.post(
            `${API_BASE_URL}/auth/refresh/`,
            {
              refresh: refreshToken
            }
          );

        const accessToken =
          response.data.access;

        localStorage.setItem(
          'access_token',
          accessToken
        );

        originalRequest.headers =
          originalRequest.headers || {};

        originalRequest.headers.Authorization =
          `Bearer ${accessToken}`;

        return api(originalRequest);
      } catch (refreshError) {
        localStorage.removeItem(
          'access_token'
        );

        localStorage.removeItem(
          'refresh_token'
        );

        localStorage.removeItem(
          'user'
        );

        window.location.href =
          '/login';

        return Promise.reject(
          refreshError
        );
      }
    }

    return Promise.reject(error);
  }
);

export const authAPI = {
  register: (data) =>
    api.post(
      '/auth/register/',
      data
    ),

  login: (data) =>
    api.post(
      '/auth/login/',
      data
    ),

  getProfile: () =>
    api.get('/users/me/'),

  updateProfile: (data) =>
    api.patch(
      '/users/update_profile/',
      data
    )
};

/*
 * Video API
 *
 * Feed is now paginated.
 *
 * Example:
 * /videos/?page=1&page_size=10
 */
export const videoAPI = {
  getFeed: (
    page = 1,
    pageSize = 10,
    options = {}
  ) => {
    const cacheKey =
      `feed_cache_${page}_${pageSize}`;

    const cacheTimeKey =
      `${cacheKey}_time`;

    const now = Date.now();

    /*
     * Only cache the first page.
     * Later pages should remain fresh.
     */
    if (
      options.cache !== false
    ) {
      try {
        const cached =
          sessionStorage.getItem(
            cacheKey
          );

        const cacheTime =
          sessionStorage.getItem(
            cacheTimeKey
          );

        if (
          cached &&
          cacheTime &&
          now -
            Number.parseInt(
              cacheTime,
              10
            ) <
            30000
        ) {
          return Promise.resolve({
            data: JSON.parse(
              cached
            )
          });
        }
      } catch (_) {
        /*
         * Ignore browser storage
         * failures.
         */
      }
    }

    return api
      .get('/videos/', {
        params: {
          page,
          page_size: pageSize
        }
      })
      .then((response) => {
        if (
          options.cache !== false
        ) {
          try {
            sessionStorage.setItem(
              cacheKey,
              JSON.stringify(
                response.data
              )
            );

            sessionStorage.setItem(
              cacheTimeKey,
              now.toString()
            );
          } catch (_) {}
        }

        return response;
      });
  },

  getVideo: (id) =>
    api.get(
      `/videos/${id}/`
    ),

  uploadVideo: (
    formData,
    onUploadProgress
  ) =>
    api.post(
      '/videos/upload/',
      formData,
      {
        headers: {
          'Content-Type':
            'multipart/form-data'
        },

        ...(onUploadProgress
          ? {
              onUploadProgress
            }
          : {})
      }
    ),

  likeVideo: (id) =>
    api.post(
      `/videos/${id}/like/`
    ),

  unlikeVideo: (id) =>
    api.post(
      `/videos/${id}/unlike/`
    ),

  shareVideo: (
    id,
    platform
  ) =>
    api.post(
      `/videos/${id}/share/`,
      {
        platform
      }
    ),

  getVideoStats: (id) =>
    api.get(
      `/videos/${id}/stats/`
    )
};

export const walletAPI = {
  getBalance: () =>
    api.get(
      '/wallet/balance/'
    ),

  getTransactions: () =>
    api.get(
      '/wallet/transactions/'
    ),

  checkWithdrawalStatus: () =>
    api.get(
      '/wallet/withdrawal_status/'
    ),

  requestWithdrawal: (data) =>
    api.post(
      '/wallet/request_withdrawal/',
      data
    ),

  transferPoints: (data) =>
    api.post(
      '/wallet/transfer/',
      data
    )
};

export const rewardAPI = {
  claimDailyLogin: () =>
    api.post(
      '/rewards/daily-login/'
    ),

  claimAdReward: (data) =>
    api.post(
      '/rewards/ad-view/',
      data
    )
};

export const favoriteAPI = {
  favoriteVideo: (id) =>
    api.post(
      `/videos/${id}/favorite/`
    ),

  getFavorites: () =>
    api.get(
      '/videos/favorites/'
    )
};

export const socialAPI = {
  followUser: (id) =>
    api.post(
      `/users/${id}/follow/`
    ),

  unfollowUser: (id) =>
    api.post(
      `/users/${id}/unfollow/`
    ),

  getFollowing: () =>
    api.get(
      '/feed/following/'
    )
};

export const messageAPI = {
  getConversations: () =>
    api.get(
      '/messages/conversations/'
    ),

  getMessages: (userId) =>
    api.get(
      `/messages/${userId}/`
    ),

  sendMessage: (
    userId,
    text
  ) =>
    api.post(
      `/messages/${userId}/`,
      {
        text
      }
    )
};

export const giftAPI = {
  giftPoints: (
    creatorId,
    amount,
    videoId
  ) =>
    api.post(
      '/gifts/send/',
      {
        creator_id:
          creatorId,
        amount,
        video_id:
          videoId
      }
    )
};

export const profileAPI = {
  getUserVideos: () =>
    api.get(
      '/videos/my/'
    ),

  getLikedVideos: () =>
    api.get(
      '/videos/liked/'
    ),

  deleteVideo: (id) =>
    api.delete(
      `/videos/${id}/delete/`
    )
};

export const verificationAPI = {
  getStatus: () =>
    api.get(
      '/users/verify/status/'
    ),

  submit: (data) =>
    api.post(
      '/users/verify/',
      data
    )
};

export default api;